import type { CSSProperties } from 'react';
import type { Participant } from '../../../../types';
import type { ColumnConfig, SortConfig } from '../../../../hooks';
import { ConfigurableTable } from '../../../../components/common';
import { renderCellContent, type FormBaseUrls } from './ParticipantCellRenderers';
import { getColumnMinWidth } from './participantColumnConfig';

export interface ParticipantsTableViewProps {
  /** Whether data is loading */
  loading: boolean;
  /** Filtered participant list */
  participants: Participant[];
  /** Visible + hidden column config */
  columnConfig: ColumnConfig[];
  /** Current sort config */
  sortConfig: SortConfig | null;
  /** Sort handler */
  onSort: (key: string) => void;
  /** Total visible column count (for colSpan on empty state) */
  visibleColumnCount: number;
  /** Whether any filters are active */
  hasActiveFilters: boolean;
  /** Clear all filters */
  onClearFilters: () => void;
  // Pagination
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Row click opens the participant detail modal */
  onRowClick: (participant: Participant) => void;
  /** Base form URLs for feedback surveys (midpoint, endpoint, post_session) */
  formBaseUrls?: FormBaseUrls;
}

export function ParticipantsTableView({
  loading,
  participants,
  columnConfig,
  sortConfig,
  onSort,
  visibleColumnCount,
  hasActiveFilters,
  onClearFilters,
  page,
  pageSize,
  totalCount,
  totalPages: _totalPages,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  formBaseUrls,
}: ParticipantsTableViewProps) {
  const getRowStyle = (participant: Participant): CSSProperties | undefined => {
    const isWithdrawn = participant.status === 'withdrawn';
    const style: CSSProperties = {};
    if (participant.has_open_flag && !isWithdrawn) {
      style.backgroundColor = '#FEF3E7';
    }
    // MED-1367: grey text instead of a faded row, so withdrawn rows stay
    // readable. The avatar fades in the name cell.
    if (isWithdrawn) {
      style.color = '#666666';
    }
    return Object.keys(style).length > 0 ? style : undefined;
  };

  return (
    <ConfigurableTable<Participant>
      data={participants}
      columns={columnConfig}
      getRowKey={(p) => p.id}
      renderCell={(participant, column) =>
        renderCellContent(column, participant, formBaseUrls)
      }
      sortConfig={sortConfig}
      onSort={onSort}
      loading={loading}
      page={page}
      pageSize={pageSize}
      totalCount={totalCount}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      visibleColumnCount={visibleColumnCount}
      emptyMessage="No participants match the current filters"
      hasActiveFilters={hasActiveFilters}
      onClearFilters={onClearFilters}
      stickyFirstColumn
      getColumnMinWidth={getColumnMinWidth}
      getRowStyle={getRowStyle}
      onRowClick={onRowClick}
      entityLabel="participants"
    />
  );
}
